import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  forwardRef,
  NgModule,
  Provider,
} from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  NgxGoogleAnalyticsModule,
  NgxGoogleAnalyticsRouterModule,
} from 'ngx-google-analytics';

import { environment } from '@environments/environment';
import { ImageUploadModule } from '@image-upload/image-upload.module';
import { GlobalLoaderInterceptor } from '@interceptors/global-loader-interceptor';
import { ImpersonationInterceptor } from '@interceptors/impersonation-interceptor';
import { getHunPaginatorIntl } from '@shared/interfaces/hun-paginator-intl';

import { storeDevtoolsModule } from '../build-specifics/index';
import { AgeVerificationModule } from './age-verification/age-verification.module';
import { ApiModule } from './api/marketplace/api.module';
import { ApiModule as PriceSyncApiModule } from './api/price-synchronizer/api.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BasketDiscountsModule } from './basket-discounts/basket-discounts.module';
import { DiagnosticErrorHandler } from './diagnostics/services/diagnostic-error-handler.service';
import { RouteHistoryService } from './diagnostics/services/route-history.service';
import { UserActionService } from './diagnostics/services/user-action.service';
import { EasylotAdminSharedModule } from './easylot-admin/modules/easylot-admin-shared/easylot-admin-shared.module';
import { ImpersonationEffects } from './easylot-admin/modules/impersonation/store/impersonation.effects';
import { ImpersonationFeature } from './easylot-admin/modules/impersonation/store/impersonation.slice';
import { MerchantApprovalsModule } from './easylot-admin/modules/merchant-approvals/merchant-approvals.module';
import { ErrorModule } from './error/error.module';
import { ApiAuthInterceptor } from './interceptors/api-auth-interceptor';
import { ApiErrorInterceptor } from './interceptors/api-error-interceptor';
import { HalAcceptInterceptor } from './interceptors/hal-accept-interceptor';
import { TraceInterceptor } from './interceptors/trace-interceptor';
import { LayoutModule } from './layout/layout.module';
import { MerchantAccountModule } from './merchant-account/merchant-account.module';
import { ProductImportModule } from './product-import/product-import.module';
import { SandboxModule } from './sandbox/sandbox.module';
import { SegmentDiscountsModule } from './segment-discounts/segment-discounts.module';
import { SharedModule } from './shared/shared.module';
import { TelesalesModule } from './telesales/telesales.module';
import { VipDiscountsBuyingModule } from './vip-discounts/modules/vip-discounts-buying/vip-discounts-buying.module';
import { VipDiscountsSellingModule } from './vip-discounts/modules/vip-discounts-selling/vip-discounts-selling.module';
import { VipDiscountsModule } from './vip-discounts/vip-discounts.module';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

export const API_ERROR_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ApiErrorInterceptor),
  multi: true,
};

export const API_AUTH_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ApiAuthInterceptor),
  multi: true,
};

export const TRACE_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => TraceInterceptor),
  multi: true,
};

export const GLOBAL_LOADER_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => GlobalLoaderInterceptor),
  multi: true,
};
export const IMPERSONATION_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => ImpersonationInterceptor),
  multi: true,
};
export const HAL_ACCEPT_INTERCEPTOR_PROVIDER: Provider = {
  provide: HTTP_INTERCEPTORS,
  useExisting: forwardRef(() => HalAcceptInterceptor),
  multi: true,
};

/**
 * Eagerly instantiates RouteHistoryService at bootstrap so its
 * router-events subscription is live before the first navigation —
 * the diagnostic bundle will then carry the user's navigation history.
 * APP_INITIALIZER is used (not an AppComponent constructor parameter)
 * to avoid the unused-parameter code smell and to survive any future
 * AppComponent refactor.
 */
export function eagerInitRouteHistory(
  _history: RouteHistoryService
): () => Promise<void> {
  return () => Promise.resolve();
}

export const ROUTE_HISTORY_INITIALIZER_PROVIDER: Provider = {
  provide: APP_INITIALIZER,
  useFactory: eagerInitRouteHistory,
  deps: [RouteHistoryService],
  multi: true,
};

export function eagerInitUserAction(
  _userAction: UserActionService
): () => Promise<void> {
  return () => Promise.resolve();
}

export const USER_ACTION_INITIALIZER_PROVIDER: Provider = {
  provide: APP_INITIALIZER,
  useFactory: eagerInitUserAction,
  deps: [UserActionService],
  multi: true,
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    LayoutModule,
    SharedModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    StoreModule.forRoot({}),
    StoreModule.forFeature(ImpersonationFeature),
    EffectsModule.forRoot([]),
    EffectsModule.forFeature([ImpersonationEffects]),
    ErrorModule.forRoot(),
    NgxGoogleAnalyticsModule.forRoot(environment.analyticsTrackingCode),
    NgxGoogleAnalyticsRouterModule,
    storeDevtoolsModule,
    AgeVerificationModule,
    ImageUploadModule,
    ProductImportModule,
    ApiModule.forRoot({ rootUrl: '/api/marketplace/v1' }),
    PriceSyncApiModule.forRoot({ rootUrl: '/api/price-synchronizer/v1' }),
    MerchantApprovalsModule,
    EasylotAdminSharedModule,
    SandboxModule,
    VipDiscountsModule,
    TelesalesModule,
    MerchantAccountModule,
    VipDiscountsSellingModule,
    VipDiscountsBuyingModule,
    SegmentDiscountsModule,
    BasketDiscountsModule,
  ],
  providers: [
    ApiErrorInterceptor,
    ApiAuthInterceptor,
    TraceInterceptor,
    GlobalLoaderInterceptor,
    ImpersonationInterceptor,
    HalAcceptInterceptor,
    API_ERROR_INTERCEPTOR_PROVIDER,
    API_AUTH_INTERCEPTOR_PROVIDER,
    TRACE_INTERCEPTOR_PROVIDER,
    GLOBAL_LOADER_INTERCEPTOR_PROVIDER,
    IMPERSONATION_INTERCEPTOR_PROVIDER,
    HAL_ACCEPT_INTERCEPTOR_PROVIDER,
    ROUTE_HISTORY_INITIALIZER_PROVIDER,
    USER_ACTION_INITIALIZER_PROVIDER,
    { provide: ErrorHandler, useClass: DiagnosticErrorHandler },
    { provide: MatPaginatorIntl, useValue: getHunPaginatorIntl() },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
